import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from 'react-leaflet';
import { MAP_TILE_URL, MAP_TILE_ATTRIBUTION } from '../../lib/mapTiles';
import L from 'leaflet';
import { request } from '../../api/client';
import { Tag, Switch, Spinner } from '../../ui';

// Disable default icon fallback (we use DivIcon)
delete (L.Icon.Default.prototype as any)._getIconUrl;

interface Style {
  color: string;
  label: string;
}

const STYLES: Record<string, Style> = {
  retailer: { color: '#339022', label: 'Retailer' },
  warehouse: { color: '#1677FF', label: 'Warehouse' },
  sales: { color: '#D9363E', label: 'Sales Live' },
};

function pinIcon(style: Style, size = 32): L.DivIcon {
  const html = `
    <div style="position:relative;width:${size}px;height:${size}px;transform:translate(-50%,-100%);filter:drop-shadow(0 2px 3px rgba(0,0,0,.35));">
      <svg viewBox="0 0 24 32" width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z"
              fill="${style.color}" stroke="white" stroke-width="1.5"/>
        <circle cx="12" cy="12" r="5" fill="white"/>
      </svg>
    </div>`;
  return L.divIcon({
    html, className: '', iconSize: [size, size], iconAnchor: [size / 2, size],
    tooltipAnchor: [0, -size + 4],
  });
}

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Records without real coordinates would otherwise drop a pin at [0,0] (Gulf of
// Guinea) or throw on NaN — keep only finite, non-zero lat/lng.
const hasCoords = (x: any) => {
  const lat = Number(x?.latitude);
  const lng = Number(x?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
};

export function MapDashboard() {
  const navigate = useNavigate();
  const [showRetailers, setShowRetailers] = useState(true);
  const [showWarehouses, setShowWarehouses] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-map'],
    queryFn: () => request<any>({ url: '/dashboard/map' }),
  });

  const icons = useMemo(() => ({
    retailer: pinIcon(STYLES.retailer),
    warehouse: pinIcon(STYLES.warehouse),
  }), []);

  if (isLoading) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-3 text-muted">
        <Spinner className="size-7 text-brand-600" />
        <span className="text-sm">Loading map…</span>
      </div>
    );
  }

  const retailers = ((data?.retailers ?? []) as any[]).filter(hasCoords);
  const warehousesAll = ((data?.warehouses ?? []) as any[]).filter(hasCoords);
  const warehouses = warehousesAll.filter((w) => w.type === 'WAREHOUSE');

  return (
    <div className="space-y-3">
      {/* Header: title + counts + legend toggles */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-lg font-bold text-content">Map Dashboard</h1>
          <div className="flex flex-wrap gap-1.5">
            <Tag tone="green">{retailers.length} retailers</Tag>
            <Tag tone="blue">{warehouses.length} warehouses</Tag>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <LegendToggle color={STYLES.retailer.color} label="Retailers" checked={showRetailers} onChange={setShowRetailers} />
          <LegendToggle color={STYLES.warehouse.color} label="Warehouses" checked={showWarehouses} onChange={setShowWarehouses} />
        </div>
      </div>

      {/* Map card */}
      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-card">
        <MapContainer center={[22.5, 80]} zoom={5} style={{ height: 'calc(100vh - 210px)', minHeight: 460, width: '100%' }}>
          <TileLayer attribution={MAP_TILE_ATTRIBUTION} url={MAP_TILE_URL} />

          {showRetailers && retailers.map((r) => (
            <Marker
              key={`r${r.id}`}
              position={[Number(r.latitude), Number(r.longitude)]}
              icon={icons.retailer}
              eventHandlers={{ click: () => navigate(`/retailers/${r.id}`) }}
            >
              <Tooltip direction="top" offset={[0, -4]} opacity={1}>
                <div className="min-w-[170px]">
                  <div className="font-semibold text-content">{r.company_name}</div>
                  <div className="text-xs text-muted">Outstanding: {inr(r.outstanding_balance)}</div>
                  <div className="mt-0.5 text-[11px] text-subtle">Click to open detail</div>
                </div>
              </Tooltip>
              <Popup>
                <div className="min-w-[200px]">
                  <div className="font-semibold text-content">{r.company_name}</div>
                  <div className="mt-0.5 text-[13px] text-muted">
                    Outstanding: <b className="text-danger-500">{inr(r.outstanding_balance)}</b>
                  </div>
                  <button type="button" onClick={() => navigate(`/retailers/${r.id}`)} className="mt-1.5 text-[13px] font-medium text-brand-700 hover:underline">
                    Open retailer →
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}

          {showWarehouses && warehouses.map((w) => (
            <Marker
              key={`w${w.id}`}
              position={[Number(w.latitude), Number(w.longitude)]}
              icon={icons.warehouse}
              eventHandlers={{ click: () => navigate(`/inventory/stock`) }}
            >
              <Tooltip direction="top" offset={[0, -4]} opacity={1}>
                <div className="min-w-[170px]">
                  <div className="font-semibold text-content">{w.name}</div>
                  <div className="text-xs text-muted">{w.code} · Warehouse</div>
                </div>
              </Tooltip>
              <Popup>
                <div className="min-w-[180px]">
                  <div className="font-semibold text-content">{w.name}</div>
                  <div className="mt-0.5 text-[13px] text-muted">Code: {w.code} · Warehouse</div>
                  <button type="button" onClick={() => navigate('/inventory/stock')} className="mt-1.5 text-[13px] font-medium text-brand-700 hover:underline">
                    Open stock →
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}

        </MapContainer>
      </div>
    </div>
  );
}

function LegendToggle({ color, label, checked, onChange }: {
  color: string; label: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-2 text-sm text-content">
      <span className="inline-block size-2.5 rounded-full" style={{ background: color }} />
      <span>{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
